import React from "react";
import { ConnectionButton } from "./WebSocketTestPageButtons";

interface Market {
  marketIndex?: string;
  id?: number | string;
  moniker?: string;
  symbol?: string;
  baseAsset?: string;
  quoteAsset?: string;
  active?: boolean;
}

interface ConfigSectionProps {
  wsURL: string;
  onWsURLChange: (value: string) => void;
  marketId: string;
  onMarketIdChange: (value: string) => void;
  markets: Market[];
  symbol: string;
  onSymbolChange: (value: string) => void;
  klineInterval: string;
  onKlineIntervalChange: (value: string) => void;
  twapWindowSize: string;
  onTwapWindowSizeChange: (value: string) => void;
  authToken: string;
  onAuthTokenChange: (value: string) => void;
  walletAddress: string | null;
  deviceID: string;
  onDeviceIDChange: (value: string) => void;
  isConnected: boolean;
  isConnecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}

export const ConfigSection: React.FC<ConfigSectionProps> = ({
  wsURL,
  onWsURLChange,
  marketId,
  onMarketIdChange,
  markets,
  symbol,
  onSymbolChange,
  klineInterval,
  onKlineIntervalChange,
  twapWindowSize,
  onTwapWindowSizeChange,
  authToken,
  onAuthTokenChange,
  walletAddress,
  deviceID,
  onDeviceIDChange,
  isConnected,
  isConnecting,
  onConnect,
  onDisconnect,
}) => {
  return (
    <div className="App-section">
      <h2>Configuration</h2>
      <div className="config-grid">
        <div>
          <label>
            WebSocket URL:
            <input
              type="text"
              value={wsURL}
              onChange={(e) => onWsURLChange(e.target.value)}
              className="config-input"
            />
          </label>
        </div>
        <div>
          <label>
            Market:
            <select
              value={marketId || ""}
              onChange={(e) => onMarketIdChange(e.target.value)}
              className="config-select"
            >
              {markets.length === 0 ? (
                <option value="">Loading markets...</option>
              ) : (
                <>
                  <option value="" disabled>
                    -- Select a market --
                  </option>
                  {markets.map((market) => {
                    const marketIdValue =
                      market.index ?? market.marketIndex ?? "";
                    const displayName = market.moniker
                      ? market.moniker
                      : market.symbol
                        ? market.symbol
                        : market.baseAsset && market.quoteAsset
                          ? `${market.baseAsset}-${market.quoteAsset}`
                          : `Market ID: ${marketIdValue}`;
                    const isActive = market.active ? " [ACTIVE]" : "";
                    return (
                      <option key={marketIdValue} value={marketIdValue}>
                        {displayName} - ID: {marketIdValue}
                        {isActive}
                      </option>
                    );
                  })}
                </>
              )}
            </select>
          </label>
        </div>
        <div>
          <label>
            Symbol (for funding_rate, deviation, index_price, mark_price,
            priceFeed, twap):
            <input
              type="text"
              value={symbol}
              onChange={(e) => onSymbolChange(e.target.value)}
              placeholder="e.g., BTC/USDT or BTC/USDC"
              className="config-input"
            />
            {symbol && !symbol.includes("/") && (
              <small className="config-error">
                ⚠ Symbol must be in format "BASE/QUOTE" (e.g., "BTC/USDC")
              </small>
            )}
          </label>
        </div>
        <div>
          <label>
            Kline Interval:
            <select
              value={klineInterval}
              onChange={(e) => onKlineIntervalChange(e.target.value)}
              className="config-select"
            >
              <option value="1m">1 minute</option>
              <option value="5m">5 minutes</option>
              <option value="15m">15 minutes</option>
              <option value="1h">1 hour</option>
              <option value="4h">4 hours</option>
              <option value="1d">1 day</option>
            </select>
          </label>
        </div>
        <div>
          <label>
            TWAP Window Size (seconds):
            <input
              type="number"
              value={twapWindowSize}
              onChange={(e) => onTwapWindowSizeChange(e.target.value)}
              placeholder="e.g., 300 for 5 minutes"
              min="1"
              className="config-input"
            />
            {twapWindowSize && (isNaN(Number(twapWindowSize)) || Number(twapWindowSize) <= 0) && (
              <small className="config-error">
                ⚠ Window size must be a positive number
              </small>
            )}
          </label>
        </div>
        <div>
          <label>
            Auth Token (for private channels):
            <input
              type="text"
              value={authToken}
              onChange={(e) => onAuthTokenChange(e.target.value)}
              placeholder="Required for user_updates (auto-filled from SDK Test Page)"
              className="config-input"
            />
            {authToken && (
              <small className="config-hint">
                ✓ Auto-filled from SDK Test Page
              </small>
            )}
          </label>
        </div>
        <div>
          <label>
            Wallet Address:
            <input
              type="text"
              value={walletAddress || ""}
              disabled={true}
              placeholder="Auto-filled from useOnboard hook"
              className={`config-input-disabled ${walletAddress ? "has-value" : ""}`}
            />
            {walletAddress && (
              <small className="config-hint">
                ✓ Auto-filled from useOnboard hook
              </small>
            )}
          </label>
        </div>
        <div>
          <label>
            Device ID:
            <input
              type="text"
              value={deviceID}
              onChange={(e) => onDeviceIDChange(e.target.value)}
              className="config-input"
            />
          </label>
        </div>
      </div>

      <ConnectionButton
        isConnected={isConnected}
        isConnecting={isConnecting}
        onConnect={onConnect}
        onDisconnect={onDisconnect}
      />
    </div>
  );
};
